import React from 'react'
import type { StatusShape } from './constants'

interface StatusIconProps {
  shape: StatusShape
  color: string
  filled: boolean
  size?: number
}

export const StatusIcon: React.FC<StatusIconProps> = ({ shape, color, filled, size = 12 }) => {
  const commonStyle: React.CSSProperties = {
    display: 'inline-block',
    width: `${size}px`,
    height: `${size}px`,
    flexShrink: 0,
  }

  switch (shape) {
    case 'circle':
      return (
        <span
          style={{
            ...commonStyle,
            borderRadius: '50%',
            backgroundColor: filled ? color : 'transparent',
            border: filled ? 'none' : `2px solid ${color}`,
          }}
        />
      )

    case 'square':
      return (
        <span
          style={{
            ...commonStyle,
            borderRadius: '2px',
            backgroundColor: filled ? color : 'transparent',
            border: filled ? 'none' : `2px solid ${color}`,
          }}
        />
      )

    case 'diamond':
      return (
        <span
          style={{
            ...commonStyle,
            transform: 'rotate(45deg)',
            backgroundColor: filled ? color : 'transparent',
            border: filled ? 'none' : `2px solid ${color}`,
          }}
        />
      )

    case 'triangle':
      return (
        <span
          style={{
            ...commonStyle,
            width: 0,
            height: 0,
            borderLeft: `${size / 2}px solid transparent`,
            borderRight: `${size / 2}px solid transparent`,
            borderBottom: `${size}px solid ${filled ? color : 'transparent'}`,
            ...(filled
              ? {}
              : {
                  position: 'relative',
                  borderBottom: `${size}px solid ${color}`,
                }),
          }}
        >
          {!filled && (
            <span
              style={{
                position: 'absolute',
                top: '2px',
                left: `${-size / 2 + 2}px`,
                width: 0,
                height: 0,
                borderLeft: `${size / 2 - 2}px solid transparent`,
                borderRight: `${size / 2 - 2}px solid transparent`,
                borderBottom: `${size - 4}px solid var(--theme-elevation-100)`,
              }}
            />
          )}
        </span>
      )

    case 'circle-check':
      return (
        <span
          style={{
            ...commonStyle,
            borderRadius: '50%',
            backgroundColor: color,
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width={size * 0.7}
            height={size * 0.7}
            viewBox="0 0 12 12"
            fill="none"
            style={{ position: 'absolute' }}
          >
            <path
              d="M2 6L5 9L10 3"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )

    case 'circle-cross':
      return (
        <span
          style={{
            ...commonStyle,
            borderRadius: '50%',
            backgroundColor: color,
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width={size * 0.6}
            height={size * 0.6}
            viewBox="0 0 12 12"
            fill="none"
            style={{ position: 'absolute' }}
          >
            <path d="M2 2L10 10M10 2L2 10" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
      )

    case 'circle-dash':
      return (
        <span
          style={{
            ...commonStyle,
            borderRadius: '50%',
            backgroundColor: color,
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width={size * 0.7}
            height={size * 0.7}
            viewBox="0 0 12 12"
            fill="none"
            style={{ position: 'absolute' }}
          >
            <path d="M3 6H9" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
      )

    default:
      return (
        <span
          style={{
            ...commonStyle,
            borderRadius: '50%',
            backgroundColor: color,
          }}
        />
      )
  }
}
